"use client";

import * as React from "react";
import {
  Check,
  ChevronsUpDown,
  InboxIcon,
  Loader2,
  Search,
  Table2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAllDataTables } from "@/hooks/use-all-data-tables";
import { cn } from "@/lib/utils";

interface DataTableIntegrationSelectorProps {
  onIntegrationsSelect?: (dataTableIds: string[]) => void;
  defaultValue?: string[];
  className?: string;
  disabled?: boolean;
}

export function DataTableIntegrationSelector({
  onIntegrationsSelect,
  defaultValue,
  className,
  disabled = false,
}: DataTableIntegrationSelectorProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  const stableDefaultValue = React.useMemo(
    () => (Array.isArray(defaultValue) ? defaultValue : []),
    [defaultValue]
  );

  const [selectedDataTables, setSelectedDataTables] = React.useState<string[]>(
    []
  );

  const { data: tables = [], isLoading } = useAllDataTables();

  React.useEffect(() => {
    setSelectedDataTables(stableDefaultValue);
  }, [stableDefaultValue]);

  const filteredTables = React.useMemo(
    () =>
      tables.filter(
        (table) =>
          table.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          table.description?.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [tables, searchQuery]
  );

  const handleSelect = (dataTableId: string) => {
    setSelectedDataTables((prev) => {
      const isRemoving = prev.includes(dataTableId);
      const nextSelected = isRemoving
        ? prev.filter((id) => id !== dataTableId)
        : [...prev, dataTableId];

      onIntegrationsSelect?.(nextSelected);

      if (!isRemoving) {
        setOpen(false);
        setSearchQuery("");
      }

      return nextSelected;
    });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "h-10 w-full justify-between font-normal text-muted-foreground",
            className
          )}
          disabled={disabled || isLoading}
        >
          <span className="truncate">Select data tables to connect</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-2"
        align="start"
        sideOffset={4}
      >
        <Input
          placeholder="Search data tables..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="mb-2 pl-8 focus-visible:ring-0"
          startIcon={
            <Search className="absolute left top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
          }
        />
        <div className="h-[200px] overflow-y-auto">
          {isLoading ? (
            <div className="p-4 text-center">
              <Loader2 className="mx-auto h-6 w-6 animate-spin" />
              <p className="mt-2 text-sm text-muted-foreground">
                Loading data tables...
              </p>
            </div>
          ) : filteredTables.length === 0 ? (
            <div className="flex flex-col items-center py-4">
              <InboxIcon className="mb-2 h-8 w-8 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                No data tables found
              </p>
            </div>
          ) : (
            filteredTables.map((table) => (
              <div
                key={table.id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(table.id);
                }}
                className={cn(
                  "flex w-full cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors",
                  selectedDataTables.includes(table.id)
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <Check
                  className={cn(
                    "h-4 w-4 shrink-0 transition-opacity",
                    selectedDataTables.includes(table.id)
                      ? "opacity-100 text-primary"
                      : "opacity-0"
                  )}
                />
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <Table2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{table.name}</p>
                    {table.description?.trim() ? (
                      <p className="truncate text-xs text-muted-foreground">
                        {table.description}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
